// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import { ArrowDown } from 'lucide';
import { useEffect, useRef, useState } from 'react';

const MAX_PREVIEW_CHARACTERS = 20_000;
const MAX_PREVIEW_LINES = 400;
const OUTPUT_TAIL_THRESHOLD_PX = 16;

export interface ToolErrorValue {
  code?: string | null;
  message: string;
}

export interface ToolInputOutputPanelProps {
  input?: unknown;
  output?: unknown;
  result?: unknown;
  error?: ToolErrorValue | null;
  exitCode?: number | null;
  cwd?: string | null;
  outputTruncated?: boolean;
  outputByteCount?: number;
  className?: string;
}

export interface ToolTextPreview {
  text: string;
  truncated: boolean;
  originalByteCount: number;
  originalLineCount: number;
}

/** Removes terminal escape sequences and non-printing controls from tool text. */
export function sanitizeToolText(value: string): string {
  return value
    .replace(/\u001b\][^\u0007]*(?:\u0007|\u001b\\)/g, '')
    .replace(/\u001b\[[0-?]*[ -/]*[@-~]/g, '')
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '')
    .replace(/[\u202a-\u202e\u2066-\u2069]/g, '');
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function describeBinary(value: unknown): string | null {
  if (value instanceof ArrayBuffer) {
    return `[Binary data omitted: ${value.byteLength} bytes]`;
  }
  if (ArrayBuffer.isView(value)) {
    return `[Binary data omitted: ${value.byteLength} bytes]`;
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return `[Binary data omitted: ${value.size} bytes]`;
  }
  return null;
}

/** Safely serializes values without interpreting them as HTML or Markdown. */
export function formatToolValue(value: unknown): string {
  const binaryDescription = describeBinary(value);
  if (binaryDescription) return binaryDescription;
  if (typeof value === 'string') return sanitizeToolText(value);
  if (value === undefined) return '[No value recorded]';

  const seen = new WeakSet<object>();
  try {
    const serialized = JSON.stringify(
      value,
      (_key, nestedValue: unknown) => {
        const nestedBinaryDescription = describeBinary(nestedValue);
        if (nestedBinaryDescription) return nestedBinaryDescription;
        if (typeof nestedValue === 'bigint') return `${nestedValue}n`;
        if (typeof nestedValue === 'function') return '[Function omitted]';
        if (typeof nestedValue === 'symbol') return '[Symbol omitted]';
        if (nestedValue && typeof nestedValue === 'object') {
          if (seen.has(nestedValue)) return '[Circular reference]';
          seen.add(nestedValue);
        }
        return nestedValue;
      },
      2
    );
    return sanitizeToolText(serialized ?? String(value));
  } catch {
    return '[Value could not be displayed safely]';
  }
}

export function createToolTextPreview(value: string): ToolTextPreview {
  const safeText = sanitizeToolText(value);
  const originalLines = safeText.split('\n');
  let text = safeText;
  let truncated = false;

  if (originalLines.length > MAX_PREVIEW_LINES) {
    const headCount = Math.floor(MAX_PREVIEW_LINES * 0.6);
    const tailCount = MAX_PREVIEW_LINES - headCount;
    const omittedLines = originalLines.length - MAX_PREVIEW_LINES;
    text = [
      ...originalLines.slice(0, headCount),
      `… ${omittedLines} lines omitted …`,
      ...originalLines.slice(-tailCount),
    ].join('\n');
    truncated = true;
  }

  if (text.length > MAX_PREVIEW_CHARACTERS) {
    const headCount = Math.floor(MAX_PREVIEW_CHARACTERS * 0.6);
    const tailCount = MAX_PREVIEW_CHARACTERS - headCount;
    const omittedCharacters = text.length - MAX_PREVIEW_CHARACTERS;
    text = `${text.slice(0, headCount)}\n… ${omittedCharacters} characters omitted …\n${text.slice(-tailCount)}`;
    truncated = true;
  }

  return {
    text,
    truncated,
    originalByteCount: utf8ByteLength(safeText),
    originalLineCount: originalLines.length,
  };
}

function ToolValueSection({ label, value }: { label: string; value: unknown }) {
  const preview = createToolTextPreview(formatToolValue(value));
  return (
    <section className="min-w-0" aria-label={`${label} details`}>
      <h4 className="m-0 mb-1.5 text-label-xs font-semibold uppercase tracking-wide text-ds-text-neutral-muted-default">
        {label}
      </h4>
      <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-solid border-ds-border-neutral-subtle-default bg-code-surface p-3 font-mono text-label-xs text-ds-text-neutral-default-default">
        {preview.text}
      </pre>
      {preview.truncated && (
        <p className="m-0 mt-1 text-label-xs text-ds-text-neutral-muted-default">
          Preview truncated from {preview.originalByteCount.toLocaleString()}{' '}
          bytes across {preview.originalLineCount.toLocaleString()} lines.
        </p>
      )}
    </section>
  );
}

function StreamingOutput({
  output,
  outputTruncated = false,
  outputByteCount,
}: {
  output: unknown;
  outputTruncated?: boolean;
  outputByteCount?: number;
}) {
  const preview = createToolTextPreview(formatToolValue(output));
  const viewportRef = useRef<HTMLPreElement>(null);
  const previousTextRef = useRef('');
  const [followingTail, setFollowingTail] = useState(true);
  const [hasUnseenOutput, setHasUnseenOutput] = useState(false);

  useEffect(() => {
    if (previousTextRef.current === preview.text) return;
    previousTextRef.current = preview.text;
    const viewport = viewportRef.current;
    if (followingTail && viewport) {
      viewport.scrollTop = viewport.scrollHeight;
      setHasUnseenOutput(false);
    } else if (!followingTail) {
      setHasUnseenOutput(true);
    }
  }, [followingTail, preview.text]);

  const jumpToLatest = () => {
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
    setFollowingTail(true);
    setHasUnseenOutput(false);
  };

  return (
    <section className="relative min-w-0" aria-label="Output details">
      <h4 className="m-0 mb-1.5 text-label-xs font-semibold uppercase tracking-wide text-ds-text-neutral-muted-default">
        Output
      </h4>
      <pre
        ref={viewportRef}
        tabIndex={0}
        aria-label="Tool output"
        className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-solid border-ds-border-neutral-subtle-default bg-code-surface p-3 font-mono text-label-xs text-ds-text-neutral-default-default"
        onScroll={(event) => {
          const viewport = event.currentTarget;
          const atTail =
            viewport.scrollHeight -
              viewport.clientHeight -
              viewport.scrollTop <=
            OUTPUT_TAIL_THRESHOLD_PX;
          setFollowingTail(atTail);
          if (atTail) setHasUnseenOutput(false);
        }}
      >
        {preview.text}
      </pre>
      {preview.truncated && (
        <p className="m-0 mt-1 text-label-xs text-ds-text-neutral-muted-default">
          Preview truncated from {preview.originalByteCount.toLocaleString()}{' '}
          bytes across {preview.originalLineCount.toLocaleString()} lines.
        </p>
      )}
      {outputTruncated && (
        <p className="m-0 mt-1 text-label-xs text-ds-text-neutral-muted-default">
          Output was truncated before rendering
          {outputByteCount != null
            ? ` after ${outputByteCount.toLocaleString()} received bytes`
            : ''}
          .
        </p>
      )}
      {hasUnseenOutput && (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1 rounded-full border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default px-2 py-1 text-label-xs font-medium text-ds-text-neutral-default-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring-brand-default-focus"
          onClick={jumpToLatest}
        >
          <MorphIcon icon={ArrowDown} aria-hidden className="h-3.5 w-3.5" />
          Jump to latest
        </button>
      )}
    </section>
  );
}

export function ToolInputOutputPanel({
  input,
  output,
  result,
  error,
  exitCode,
  cwd,
  outputTruncated,
  outputByteCount,
  className,
}: ToolInputOutputPanelProps) {
  const hasOutput = output !== undefined && output !== null && output !== '';
  return (
    <div className={cn('flex min-w-0 flex-col gap-4', className)}>
      {(cwd || exitCode != null) && (
        <dl className="m-0 grid min-w-0 grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-label-xs">
          {cwd && (
            <>
              <dt className="text-ds-text-neutral-muted-default">
                Working directory
              </dt>
              <dd className="m-0 min-w-0 break-all font-mono text-ds-text-neutral-default-default">
                {sanitizeToolText(cwd)}
              </dd>
            </>
          )}
          {exitCode != null && (
            <>
              <dt className="text-ds-text-neutral-muted-default">Exit code</dt>
              <dd className="m-0 font-mono text-ds-text-neutral-default-default">
                {exitCode}
              </dd>
            </>
          )}
        </dl>
      )}

      {input !== undefined && <ToolValueSection label="Input" value={input} />}
      {hasOutput && (
        <StreamingOutput
          output={output}
          outputTruncated={outputTruncated}
          outputByteCount={outputByteCount}
        />
      )}
      {result !== undefined && (
        <ToolValueSection label="Result" value={result} />
      )}
      {error && (
        <section
          className="rounded-lg border border-solid border-ds-border-error-subtle-default bg-ds-bg-error-subtle-default p-3"
          aria-label="Tool error"
        >
          <h4 className="m-0 mb-1 text-label-xs font-semibold uppercase tracking-wide text-ds-text-error-default-default">
            Error{error.code ? ` · ${sanitizeToolText(error.code)}` : ''}
          </h4>
          <p className="m-0 whitespace-pre-wrap break-words text-body-sm text-ds-text-error-default-default">
            {sanitizeToolText(error.message)}
          </p>
        </section>
      )}

      {input === undefined && !hasOutput && result === undefined && !error && (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          No tool details were recorded.
        </p>
      )}
    </div>
  );
}
